import type { CardListItem } from "@hobbyshopify/backend-client";
import { Link } from "react-router-dom";
import { cardTitle, formatCents, gradeLabel } from "../core/primitives/card-display";

/** "Priced 4 Jul" — short, since it sits inline with the price. */
function formatPricedAt(at: Date): string {
  return at.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * What the stock line says.
 *
 * Three states, not two, and the third is the point: a card the shop chose not
 * to count is not "0 in stock". Saying so would put a shortage against every
 * single on the floor.
 */
function StockBadge({ trackStock, available }: { trackStock: boolean; available: number }) {
  if (!trackStock) return <span className="badge badge-neutral">Untracked</span>;
  if (available > 0) return <span className="badge badge-neutral">{available} available</span>;
  return <span className="badge badge-warn">Out of stock</span>;
}

/**
 * One card in the shop's catalogue.
 *
 * One price, because the card has one. The old inventory row showed the shelf
 * price and a "market" figure side by side as peers with no way to tell which
 * mattered — the second of those was a guess, and this row does not pretend
 * otherwise: when the provider disagrees, it says so as a warning rather than
 * as a second column.
 */
export function CardRow({
  item,
  busy,
  canEdit = true,
  onSetPrice,
  onRefreshPrice,
  onArchive,
  onSetStock,
  onHistory,
  available,
  onStep,
}: {
  item: CardListItem;
  busy?: boolean;
  canEdit?: boolean;
  /** The count to show, with unsent taps applied. */
  available?: number;
  /** One copy more or fewer, from the row. */
  onStep?: (step: number) => void;
  onSetPrice: () => void;
  onRefreshPrice: () => void;
  onArchive: () => void;
  onSetStock: () => void;
  onHistory: () => void;
}) {
  const { card } = item;
  const shown = available ?? card.available;
  const title = cardTitle(card);
  const subtitle = [card.setName, card.parallelType, gradeLabel(card)].filter(Boolean).join(" · ");

  return (
    <div className="rounded-row border border-line bg-surface p-3 sm:p-4">
      <div className="flex gap-3">
        <Link to={`/cards/${card.id}`} className="h-12 w-12 shrink-0 overflow-hidden rounded-thumb bg-surface-strong">
          {card.imageUrl ? (
            <img src={card.imageUrl} alt={title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-faint">No img</div>
          )}
        </Link>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <Link to={`/cards/${card.id}`} className="line-clamp-2 text-sm font-medium text-white hover:text-accent">
              {title}
            </Link>
            {card.trackStock && canEdit && onStep ? (
              <fieldset className="flex shrink-0 items-center gap-1 border-0 p-0" aria-label="Stock">
                <button
                  type="button"
                  className="btn-secondary btn-xs w-8"
                  onClick={() => onStep(-1)}
                  disabled={busy || shown <= 0}
                  aria-label="One copy fewer"
                >
                  −
                </button>
                <span
                  className={`min-w-[2.5rem] text-center text-sm font-semibold ${shown > 0 ? "text-white" : "text-warn"}`}
                  aria-live="polite"
                  title={shown > 0 ? `${shown} available` : "Out of stock"}
                >
                  {shown}
                </span>
                <button
                  type="button"
                  className="btn-secondary btn-xs w-8"
                  onClick={() => onStep(1)}
                  disabled={busy}
                  aria-label="One copy more"
                >
                  +
                </button>
              </fieldset>
            ) : (
              <StockBadge trackStock={card.trackStock} available={shown} />
            )}
          </div>

          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            {card.currentPriceCents == null ? (
              // Not an error: the lookup found nothing and nobody has typed a
              // number yet. The card is catalogued and not for sale.
              <span className="badge badge-warn">Not priced</span>
            ) : (
              <span className="text-sm font-semibold text-accent">{formatCents(card.currentPriceCents)}</span>
            )}
            {subtitle && <span className="truncate text-xs text-faint">{subtitle}</span>}
            {card.floorPriceCents != null && (
              <span className="text-xs text-faint" title="The shop will not sell below this">
                floor {formatCents(card.floorPriceCents)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div>
        {/* The provider only appears when it disagrees — so seeing it at all
            means something, and Refresh price is about to overwrite it. */}
        {item.providerPriceCents != null && (
          <p className="mt-0.5 text-xs text-warn">
            Shopify says {formatCents(item.providerPriceCents)} — Refresh price will overwrite it.
          </p>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {card.lastPricedAt && <span className="text-xs text-faint">Priced {formatPricedAt(card.lastPricedAt)}</span>}
          {/* Named for what they do rather than for the shape of the dialog:
              one asks the merchant for a number, the other asks the market. */}
          {canEdit && (
            <>
              <button type="button" onClick={onSetPrice} disabled={busy} className="btn-chip">
                Set price
              </button>
              <button type="button" onClick={onRefreshPrice} disabled={busy} className="btn-chip">
                Refresh price
              </button>
              <button type="button" onClick={onSetStock} disabled={busy} className="btn-chip">
                {card.trackStock ? "Stock" : "Track stock"}
              </button>
              <button type="button" onClick={onArchive} disabled={busy} className="btn-chip">
                Archive
              </button>
            </>
          )}
          <button type="button" onClick={onHistory} disabled={busy} className="btn-ghost">
            History
          </button>
        </div>
      </div>
    </div>
  );
}
